import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Lead, ApiResponse } from '@crypto-factory/shared-types';

const API_BASE = import.meta.env.VITE_API_URL || '';

async function fetchLeads(): Promise<Lead[]> {
    // Get auth token
    // @ts-expect-error Clerk global
    const clerk = window.Clerk;
    const token = clerk?.session ? await clerk.session.getToken() : '';

    const res = await fetch(`${API_BASE}/api/admin/leads`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    if (!res.ok) throw new Error('Failed to fetch leads');
    const data: ApiResponse<Lead[]> = await res.json();
    if (!data.success) throw new Error(data.error || 'Unknown error');
    return data.data!;
}

export default function LeadsInbox() {
    const [filter, setFilter] = useState<string>('all');

    const { data: leads = [], isLoading, error } = useQuery({
        queryKey: ['leads'],
        queryFn: fetchLeads,
    });

    const filteredLeads = leads.filter((lead) => {
        if (filter === 'all') return true;
        return lead.type === filter;
    });

    const handleExportCSV = () => {
        const headers = ['ID', 'Type', 'Email', 'Name', 'Status', 'Created'];
        const rows = filteredLeads.map((lead) => [
            lead.id,
            lead.type,
            lead.email,
            lead.name || '',
            lead.status,
            new Date(lead.createdAt).toLocaleDateString(),
        ]);

        const csv = [headers, ...rows]
            .map((row) => row.map((cell) => `"${cell}"`).join(','))
            .join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    if (error) {
        return (
            <div className="text-center py-12">
                <p className="text-red-500">Failed to load leads</p>
                <p className="text-zinc-500 text-sm mt-2">{String(error)}</p>
            </div>
        );
    }

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-white font-display mb-2">
                        Leads Inbox
                    </h1>
                    <p className="text-zinc-400">
                        {leads.length} total leads captured
                    </p>
                </div>
                <button
                    onClick={handleExportCSV}
                    disabled={filteredLeads.length === 0}
                    className="btn-ghost"
                >
                    📥 Export CSV
                </button>
            </div>

            {/* Filters */}
            <div className="flex gap-2 mb-6">
                {['all', 'contact', 'newsletter', 'speaking', 'course'].map((type) => (
                    <button
                        key={type}
                        onClick={() => setFilter(type)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === type
                                ? 'bg-neon-green/20 text-neon-green border border-neon-green/30'
                                : 'bg-void text-zinc-400 border border-white/10 hover:border-white/20'
                            }`}
                    >
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                ))}
            </div>

            {/* Table */}
            <div className="card overflow-hidden">
                {isLoading ? (
                    <div className="py-12 text-center text-zinc-500">
                        Loading leads...
                    </div>
                ) : filteredLeads.length === 0 ? (
                    <div className="py-12 text-center text-zinc-500">
                        No leads found
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="border-b border-white/10">
                            <tr className="text-left text-sm text-zinc-500">
                                <th className="py-3 px-4">Email</th>
                                <th className="py-3 px-4">Name</th>
                                <th className="py-3 px-4">Type</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4">Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLeads.map((lead) => (
                                <tr
                                    key={lead.id}
                                    className="border-b border-white/5 hover:bg-white/5"
                                >
                                    <td className="py-3 px-4 text-white font-medium">
                                        {lead.email}
                                    </td>
                                    <td className="py-3 px-4 text-zinc-400">
                                        {lead.name || '—'}
                                    </td>
                                    <td className="py-3 px-4">
                                        <span className="px-2 py-1 bg-void rounded text-xs text-zinc-400">
                                            {lead.type}
                                        </span>
                                    </td>
                                    <td className="py-3 px-4">
                                        <StatusBadge status={lead.status} />
                                    </td>
                                    <td className="py-3 px-4 text-zinc-500 text-sm">
                                        {new Date(lead.createdAt).toLocaleDateString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function StatusBadge({ status }: { status: string }) {
    const colors: Record<string, string> = {
        new: 'bg-blue-500/20 text-blue-400',
        contacted: 'bg-yellow-500/20 text-yellow-400',
        converted: 'bg-green-500/20 text-green-400',
        archived: 'bg-zinc-500/20 text-zinc-400',
    };

    return (
        <span className={`px-2 py-1 rounded text-xs ${colors[status] || colors.new}`}>
            {status}
        </span>
    );
}
